// HomeNanoox.jsx
import React, { Suspense, lazy, useState, useMemo, useEffect, useCallback } from "react";
import { TooltipModeProvider } from "./context/TooltipModeContext";
import { TooltipProvider } from "./context/TooltipContext";
import { ModuleControlsProvider } from "./context/ModuleControlsContext";
import NanooxLayout from "./layout/NanooxLayout";
import GenerarView from "./components/GenerarView";
import ModuleCard from "./components/ModuleCard";
import Cargando from "./ui/Cargando";
import "./Home.css";

const GeneradorO23DView = lazy(() => import("../3d/GeneradorO23DView"));
const Nanoox3DView = lazy(() => import("../3d/Nanoox3DView"));

const MODULOS_POR_PAGINA = 2;
const DRAG_DROP_STORAGE_KEY = "nanoox.drag-drop-enabled";

function getInitialDragDropEnabled() {
  if (typeof window === "undefined") {
    return false;
  }

  return window.localStorage.getItem(DRAG_DROP_STORAGE_KEY) === "true";
}

function hasRenderableModules(datos) {
  return Array.isArray(datos?.modulos) && datos.modulos.some((modulo) =>
    Array.isArray(modulo?.cans) && modulo.cans.some((can) =>
      Array.isArray(can?.bombas) && can.bombas.length > 0
    )
  );
}

export function resolveCenterViewState(datos = {}, loadingCenter = false) {
  if (loadingCenter) {
    return { kind: "loading", message: "Cargando datos del centro." };
  }

  const dataStatus = datos?.__dataStatus || datos?.data_status;
  const detail =
    datos?.__dataStatusDetail ||
    datos?.data_status_detail ||
    datos?.__sinDatos ||
    datos?.error ||
    "";

  if (dataStatus === "comap_only") {
    return {
      kind: "comap_only",
      message: detail || "El centro reporta solamente datos de generadores COMAP.",
    };
  }

  const legacyAccessError =
    typeof datos?.mensaje === "string" &&
    datos.mensaje.includes("Error intentando acceder al centro");
  if (
    dataStatus === "sin_estructura" ||
    dataStatus === "error" ||
    Boolean(datos?.__sinDatos) ||
    Boolean(datos?.error) ||
    legacyAccessError
  ) {
    return {
      kind: "unavailable",
      message: detail || datos?.mensaje || "Centro no disponible.",
    };
  }

  if (hasRenderableModules(datos)) {
    return { kind: "ready", message: "" };
  }

  return {
    kind: "empty",
    message:
      detail ||
      "La consulta terminó, pero no hay datos recientes de módulos, CAN o jaulas.",
  };
}

export default function HomeNanoox({ version, loadingCenter , datos, generadores, translate ,handleClick ,openMenuIdioma ,handleCloseIdioma ,handleLanguageChange, idioma, onSelectCenter, showDashboard, setShowDashboard, selectedCenter}) {
  const [softwareConfigs, setSoftwareConfigs] = useState(null);
  const [configured, setConfigured] = useState(false);
  const [controlType, setControlType] = useState("PS");
  const [page, setPage] = useState(0);
  const [password, setPassword] = useState("");
  const [userLevel] = useState(2);
  const [selectedGeneratorKey, setSelectedGeneratorKey] = useState(null);

  const [activeView, setActiveView] = useState("nanoox");
  const [activeStage, setActiveStage] = useState("generator");
  const [dragDropEnabled, setDragDropEnabled] = useState(getInitialDragDropEnabled);

  const selectedGeneratorData = datos?.generators_comap ?? null;

  // La vista de generador COMAP solo es alcanzable desde los iconos del AppBar,
  // que se alimentan de los generadores FSM antiguos. Un centro COMAP puro no
  // tiene esos generadores, asi que se expone un acceso propio en el AppBar.
  const hasComapGenerators = useMemo(() => {
    const generators = datos?.generators_comap?.generators;
    return Boolean(
      generators &&
        typeof generators === "object" &&
        Object.keys(generators).length > 0
    );
  }, [datos?.generators_comap]);


  const handleSelectGeneratorView = useCallback((generatorKey) => {
    if (generatorKey) {
      const indexedGeneratorMatch = /^gen(\d+)$/.exec(generatorKey);
      const generatorIndex = indexedGeneratorMatch
        ? Number(indexedGeneratorMatch[1]) - 1
        : -1;
      const fsmGenerator = Array.isArray(datos?.generadores)
        ? datos.generadores[generatorIndex]
        : null;
      const normalizedGeneratorKey =
        fsmGenerator?.generador_name ??
        fsmGenerator?.generator_name ??
        (indexedGeneratorMatch
          ? `generator_${indexedGeneratorMatch[1]}`
          : generatorKey);

      setSelectedGeneratorKey(normalizedGeneratorKey);
    }
    setActiveView("generar");
  }, [datos?.generadores]);


  const umbrales = datos.umbrales;
  const historialControl = datos.historial_control
  // console.log('datos', datos)

  // * ----------------------------- Estado para notificaciones -----------------------------
  //? - notificationOpen: Indica si la notificación está abierta.
  const [notificationOpen, setNotification_open] = useState(false)
  const [notification_msg, setNotification_msg] = useState('')
  const [notification_status, setNotification_status] = useState('success')

  

  // === Computados ===

  const modulos = useMemo(() => {
    if (!Array.isArray(datos?.modulos)) return [];
    
    return datos.modulos
      .filter(m => m != null) // no nulos
      .filter(m => {
        // verificar si el módulo tiene al menos una can con bombas
        const cansConBombas = (m.cans || []).filter(c => Array.isArray(c?.bombas) && c.bombas.length > 0);
        return cansConBombas.length > 0;
      });
  }, [datos?.modulos]);

  const centerViewState = useMemo(
    () => resolveCenterViewState(datos, loadingCenter),
    [datos, loadingCenter]
  );
  const displayedActiveView =
    centerViewState.kind === "comap_only" ? "generar" : activeView;

  useEffect(() => {
    if (centerViewState.kind === "comap_only" && activeView !== "generar") {
      setActiveView("generar");
    }
  }, [centerViewState.kind, activeView]);



  function calcularPromediosTemperatura(modulos) {
    if (!Array.isArray(modulos)) return null;

    let totalTemp = 0;
    let totalBombas = 0;

    const resultados = modulos.map((modulo) => {
      let tempModulo = 0;
      let bombasModulo = 0;
      const jaulas = [];

      // Recorremos cada CAN dentro del módulo
      modulo?.cans?.forEach((can) => {
        can?.bombas?.forEach((bomba) => {
          // Usa TEMP si existe, si no TEMP_bckp
          const temp = typeof bomba?.TEMP === "number" ? bomba?.TEMP : bomba?.TEMP_bckp;
          if (typeof temp === "number") {
            jaulas?.push({
              alias: bomba.alias || bomba.nombre || "Sin alias",
              temperatura: temp,
            });
            tempModulo += temp;
            totalTemp += temp;
            bombasModulo++;
            totalBombas++;
          }
        });
      });

      const promedioModulo = bombasModulo > 0 ? tempModulo / bombasModulo : null;

      return {
        moduloId: modulo.id,
        promedio: promedioModulo,
        jaulas,
      };
    });

    const promedioGlobal = totalBombas > 0 ? totalTemp / totalBombas : null;

    return {
      porModulo: resultados,
      global: promedioGlobal,
    };
  }

  function calcularAcumuladoOxigeno(modulos) {
    if (!Array.isArray(modulos)) return null;

    let acumuladoGlobal = 0;

    const resultados = (modulos ?? []).map((modulo) => {
      let acumuladoModulo = 0;
      const jaulas = [];

      (modulo?.cans ?? [])?.forEach((can) => {
        (can?.bombas ?? [])?.forEach((bomba) => {
          // Toma el primer campo válido como acumulador:
          // prioridad: Acumulador_Oxigeno → Vo → VO → acumulador_oxigeno
          const acc =
            (typeof bomba?.Acumulador_Oxigeno === "number" && bomba.Acumulador_Oxigeno) ??
            // (typeof bomba?.Vo === "number" && bomba.Vo) ??
            // (typeof bomba?.VO === "number" && bomba.VO) ??
            (typeof bomba?.acumulador_oxigeno === "number" && bomba.acumulador_oxigeno) ??
            null;

          if (typeof acc === "number") {
            jaulas.push({
              alias: bomba.alias || bomba.nombre || "Sin alias",
              acumulado: acc,
            });
            acumuladoModulo += acc;
            acumuladoGlobal += acc;
          }
        });
      });

      return {
        moduloId: modulo?.id ?? null,
        acumulado: acumuladoModulo,
        jaulas, // detalle por jaula
      };
    });

    return {
      porModulo: resultados,
      global: acumuladoGlobal,
    };
  }
  // Extrae Vo (voltaje) e Io (corriente) por jaula, agrupado por módulo.
  // Opcional: includeCan=true para incluir el nombre del CAN (HF1, HF2, etc.)
  function extraerVoIo(modulos, { includeCan = false } = {}) {
    if (!Array.isArray(modulos)) return { porModulo: [] };

    const porModulo = (modulos ?? []).map((modulo) => {
      const jaulas = [];

      (modulo?.cans ?? [])?.forEach((can) => {
        (can?.bombas ?? [])?.forEach((bomba) => {
          // Voltaje (Vo) – tolera nombres alternativos
          const vo =
            (typeof bomba?.Vo === "number" ? bomba.Vo : null) ??
            (typeof bomba?.VO === "number" ? bomba.VO : null) ??
            (typeof bomba?.voltaje === "number" ? bomba.voltaje : null);

          // Corriente (Io) – tolera nombres alternativos
          const io =
            (typeof bomba?.Io === "number" ? bomba.Io : null) ??
            (typeof bomba?.IO === "number" ? bomba.IO : null) ??
            (typeof bomba?.corriente === "number" ? bomba.corriente : null);

          // Si al menos uno de los dos es numérico, lo listamos
          if (typeof vo === "number" || typeof io === "number") {
            const item = {
              alias: bomba?.alias || bomba?.nombre || "Sin alias",
              voltaje: typeof vo === "number" ? vo : null,
              corriente: typeof io === "number" ? io : null,
            };
            if (includeCan) item.can = can?.can ?? null;
            jaulas.push(item);
          }
        });
      });

      return {
        moduloId: modulo?.id ?? null,
        jaulas,
      };
    });

    return { porModulo };
  }

  function extraerMediciones(modulos, { includeCan = false } = {}) {
    if (!Array.isArray(modulos)) return { porModulo: [] };

    const porModulo = (modulos ?? []).map((modulo) => {
      const jaulas = [];

      (modulo?.cans ?? [])?.forEach((can) => {
        (can?.bombas ?? [])?.forEach((bomba) => {
          // Valvula
          const Start =  (typeof bomba?.Start === "boolean" ? bomba.Start : null) 
          // Bomba
          const Start_Valv =  (typeof bomba?.Start_Valv === "boolean" ? bomba.Start_Valv : null) 
          // Status  
          const Status =  (typeof bomba?.Status === "number" ? bomba.Status : null) 

          // Si al menos uno de los dos es numérico, lo listamos
          if (typeof Start === "boolean" || typeof Start_Valv === "boolean" || typeof Status === "number" ) {
            const item = {
              alias: bomba?.alias || bomba?.nombre || "Sin alias",
              Start: typeof Start === "boolean" ? Start : null,
              Start_Valv: typeof Start_Valv === "boolean" ? Start_Valv : null,
              Status:    typeof Status    === "number" ? Status    : null,
            };
            if (includeCan) item.can = can?.can ?? null;
            jaulas.push(item);
          }
        });
      });

      return {
        moduloId: modulo?.id ?? null,
        jaulas,
      };
    });

    return { porModulo };
  }


  const VoIoGlobal = useMemo(() => extraerVoIo(modulos), [modulos]);
  const medicionesGlobal = useMemo(() => extraerMediciones(modulos), [modulos]);
  const TemperaturaGlobal = useMemo(() => calcularPromediosTemperatura(modulos), [modulos]);
  const AcumuladoO2Global = useMemo(() => calcularAcumuladoOxigeno(modulos), [modulos]);


  const visibleModulos = useMemo(() => {
    if (!modulos.length) return [];
    return modulos.length > MODULOS_POR_PAGINA
      ? modulos.slice(page, page + MODULOS_POR_PAGINA)
      : modulos;
  }, [modulos, page]);

  const totalPages = Math.ceil(modulos.length / MODULOS_POR_PAGINA);

  function nextPage() {
    setPage((prev) => (prev + 1 < totalPages ? prev + 1 : prev));
  }

  function prevPage() {
    setPage((prev) => (prev - 1 >= 0 ? prev - 1 : prev));
  }

  const altura = modulos.length === 1 ? "82vh"  : "40.5vh"; 
  // === Props memoizados para ModuleGrid ===
  const NanooxLayoutProps = useMemo(
    () => ({
      o2PressureOk:{configured},
      modulos,
      umbrales,
      userLevel,
      centerName:softwareConfigs?.centerName,
      lastUpdate: datos?.hora,
      generadores: datos?.generadores,
      password,
      setPassword,
      historialControl,
      controlType: controlType,
      estado: datos?.estado_centro ?? datos?.estado,
      page,
      setPage,
      totalPages,
      nextPage,
      prevPage,
      notificationOpen,
      setNotification_open,
      setNotification_msg,
      setNotification_status,
      translate ,handleClick ,openMenuIdioma ,handleCloseIdioma ,handleLanguageChange, idioma,
      onSelectCenter, showDashboard, setShowDashboard,
      version, generadores, 
      TemperaturaGlobal,
      AcumuladoO2Global,
      VoIoGlobal,
      medicionesGlobal,
      activeView: displayedActiveView,
      onViewChange: setActiveView,
      onSelectGeneratorView: handleSelectGeneratorView,
      hasComapGenerators,
      activeStage,
      dragDropEnabled,
    }),
    [datos, version, generadores,loadingCenter, controlType, softwareConfigs, page, password, userLevel, onSelectCenter,  translate ,handleClick ,openMenuIdioma ,handleCloseIdioma ,handleLanguageChange, idioma,showDashboard, setShowDashboard,  TemperaturaGlobal, AcumuladoO2Global, VoIoGlobal, medicionesGlobal, dragDropEnabled, handleSelectGeneratorView, displayedActiveView, hasComapGenerators ]
  );
  
let statusGeneradorManual = true;

// console.log('datos',datos)

if (
  generadores?.hf_status === 80 ||
  (generadores?.partida === 60 && generadores?.detencion === 100) ||
  generadores?.hf_status === 60
) {
  statusGeneradorManual = false;
}

  if (loadingCenter) {
    return <Cargando height="82vh" />;
  }

  return (
    <ModuleControlsProvider>
      <TooltipModeProvider>
        <NanooxLayout  {...NanooxLayoutProps} >
          {displayedActiveView === "nanoox" && (
            <TooltipProvider>
            <div className="home-container">
              {/* Render siempre presente */}
              {centerViewState.kind === "unavailable" ? (
                <div
                  style={{
                    height: "82vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    color: "#c00",
                    fontSize: "1.2rem",
                  }}
                >
                  {centerViewState.message}
                </div>
              ) : centerViewState.kind === "empty" ? (
                <div
                  style={{
                    height: "82vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "2rem",
                    textAlign: "center",
                    fontWeight: "bold",
                    color: "#9fb1c7",
                    fontSize: "1.05rem",
                  }}
                >
                  {centerViewState.message}
                </div>
              ) : (
                visibleModulos.map((mod, visibleIndex) => (
                  <div key={mod.id} style={{ minHeight: altura, maxHeight: altura }}>
                    <ModuleCard
                      modulo={mod}
                      modulosLength={modulos.length}
                      showControls={visibleIndex === 0}
                      controlType="PS"
                      userLevel={userLevel}
                      modulo_id={mod.id}
                      translate={translate}
                      statusGeneradorManual={statusGeneradorManual}
                      selectedCenter={selectedCenter}
                      dragDropEnabled={dragDropEnabled}
                      onToggleDragDrop={() =>
                        setDragDropEnabled((currentState) => !currentState)
                      }
                    />
                  </div>
                ))
              )}
            </div>

            </TooltipProvider>
          )}

          {displayedActiveView === "generar" && (
            <GenerarView
              modulos={modulos}
              generadores={datos?.generadores}
              generatorData={selectedGeneratorData}
              selectedGeneratorKey={selectedGeneratorKey}
              // generatorData={data}
              lastUpdate={datos?.hora}
              historialControl={historialControl}
              translate={translate}
            />
          )}

          
          {displayedActiveView === "nanoox3d" && (
            <Suspense fallback={<Cargando height="82vh" />}>
              <Nanoox3DView
                modulos={modulos}
                userLevel={userLevel}
                estadoCentro={datos?.estado_centro}
                ventilador={datos?.generador?.ventilador}
                vpsa={datos?.vpsa}
                activeStage={activeStage}
                setActiveStage={setActiveStage}
                dragDropEnabled={dragDropEnabled}
                translate={translate}
              />
            </Suspense>
          )}

          {displayedActiveView === "generador-o2-3d" && (
            <Suspense fallback={<Cargando height="82vh" />}>
              <GeneradorO23DView
                modulos={modulos}
                userLevel={userLevel}
                estadoCentro={datos?.estado_centro}
                ventilador={datos?.generador?.ventilador}
                vpsa={datos?.vpsa}
                activeStage={activeStage}
                setActiveStage={setActiveStage}
                dragDropEnabled={dragDropEnabled}
                translate={translate}
              />
            </Suspense>
          )}
        </NanooxLayout>
      </TooltipModeProvider>
    </ModuleControlsProvider>
  );
}
